import { jsx as _jsx } from "react/jsx-runtime";
import { FamilyFestFaqTypeEnum, FamilyFestFaqTypeEnumDisplay } from '@application/enums/family-fest-faq-type-enum';
import { getFamilyFestFaqCategoryOptionsByTypeAsync } from '@application/features/familyFestFaqCategory/familyFestFaqCategoryThunk';
import { useAppDispatch } from '@application/store/useStore';
import { alpha, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
/**
 * Category options of one FAQ type, fetched from the backend (get-by-type), for the FAQ dialogs'
 * category dropdown. `enabled` is false for non-FamilyFest events, which keep their own list.
 */
export const useFamilyFestFaqCategoryOptions = (faqType, enabled) => {
    const dispatch = useAppDispatch();
    const [options, setOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    useEffect(() => {
        if (!enabled)
            return;
        let cancelled = false;
        setIsLoading(true);
        dispatch(getFamilyFestFaqCategoryOptionsByTypeAsync({ familyFestFaqType: faqType }))
            .unwrap()
            .then((categories) => {
            if (!cancelled)
                setOptions(categories.map((category) => ({ label: category.name, value: category.id })));
        })
            .catch(() => {
            if (!cancelled)
                setOptions([]);
        })
            .finally(() => {
            if (!cancelled)
                setIsLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [dispatch, faqType, enabled]);
    return { options, isLoading };
};
const FAQ_TYPES = [FamilyFestFaqTypeEnum.Registration, FamilyFestFaqTypeEnum.EventPage];
/** Options for the "FAQ for" dropdowns (new category, new FAQ, category details). */
export const useFaqTypeOptions = () => {
    const { t } = useTranslation();
    return useMemo(() => FAQ_TYPES.map((faqType) => ({ value: faqType, label: t(FamilyFestFaqTypeEnumDisplay[faqType]) })), [t]);
};
/** FamilyFest only: switches the categories list between registration and event-page FAQs. */
const FaqTypeFilter = ({ value, onChange }) => {
    const theme = useTheme();
    const faqTypeOptions = useFaqTypeOptions();
    return (_jsx(ToggleButtonGroup, { size: 'small', fullWidth: true, exclusive: true, value: value, onChange: (_, nextValue) => nextValue && onChange(nextValue), sx: {
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            '& .MuiToggleButtonGroup-grouped': {
                border: 'none',
                borderRadius: 0,
                textTransform: 'none',
                fontWeight: 500,
                py: 1,
                '&:not(:last-of-type)': {
                    borderRight: `1px solid ${alpha(theme.palette.divider, 0.6)}`
                },
                '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.darker,
                    color: theme.palette.common.white,
                    '&:hover': { backgroundColor: theme.palette.primary.darker }
                },
                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) }
            }
        }, children: faqTypeOptions.map((option) => (_jsx(ToggleButton, { value: option.value, children: option.label }, option.value))) }));
};
export default FaqTypeFilter;
